import React, { useEffect, useState, useContext } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Input,
  Select,
  Option,
  Button,
  Card,
  Grid,
  FormControl,
  FormLabel,
  Modal,
  ModalDialog,
  ModalClose,
  Avatar,
  Stack,
  Table as JoyTable
} from '@mui/joy';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PageLayout from '../components/PageLayout.jsx';
import MetricCard from '../components/MetricCard';
import EnhancedButton from '../components/EnhancedButton';
import EnhancedInput from '../components/EnhancedInput';
import LoadingSkeleton, { CardSkeleton } from '../components/LoadingSkeleton';
import { supabase } from '../supabaseClient.js';
import { AuthContext } from '../context/AuthContext.jsx';

const ClientIntDocs = () => {
  const { user: _contextUser } = useContext(AuthContext);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [docs, setDocs] = useState([]);
  const [file, setFile] = useState(null);
  const [docName, setDocName] = useState('');
  const [tags, setTags] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState('user');
  const [editingDocId, setEditingDocId] = useState(null);
  const [editFields, setEditFields] = useState({
    name: '',
    tags: '',
    notes: '',
  });
  const [loading, setLoading] = useState(true);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [_editModalOpen, _setEditModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClient, setFilterClient] = useState('all');
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    fetchUser();
    fetchClients();
    fetchDocuments();
  }, []);

  const fetchUser = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    setUser(user);

    if (user?.id) {
      const { data, error } = await supabase
        .from('users')
        .select('user_type')
        .eq('id', user.id)
        .single();
      if (!error && data?.user_type) {
        setUserRole(data.user_type.toLowerCase());
      }
    }
  };

  const fetchClients = async () => {
    const { data, error } = await supabase.from('clients').select('id, name');
    if (!error) setClients(data);
    else console.error('Error fetching clients:', error);
  };

  const fetchProjects = async (clientId) => {
    if (!clientId) {
      setProjects([]);
      return;
    }
    const { data, error } = await supabase
      .from('projects')
      .select('id, name')
      .eq('client_id', clientId);
    if (!error) setProjects(data);
    else console.error('Error fetching projects:', error);
  };

  const fetchDocuments = async () => {
    setInitialLoading(true);
    setLoading(true);
    const { data, error } = await supabase
      .from('client_documents')
      .select('id, name, storage_path, uploaded_at, tags, notes, clients(name), projects(name)')
      .order('uploaded_at', { ascending: false });

    if (!error) setDocs(data || []);
    else console.error('Error fetching documents:', error);
    setLoading(false);
    setInitialLoading(false);
  };

  const handleUpload = async () => {
    if (!file || !docName || !selectedClient) return;

    const filePath = `${Date.now()}_${file.name}`;
    const { error: uploadError } = await supabase.storage
      .from('client-docs')
      .upload(filePath, file);

    if (uploadError) {
      console.error('Upload failed:', uploadError);
      return;
    }
    // Parse tags into array
    const tagsArr = tags
      ? tags.split(',').map((t) => t.trim()).filter((t) => t.length > 0)
      : [];

    const { error: insertError } = await supabase.from('client_documents').insert([
      {
        name: docName,
        storage_path: filePath,
        client_id: selectedClient,
        project_id: selectedProject || null,
        uploaded_by: user?.id || null,
        tags: tagsArr,
      },
    ]);

    if (insertError) {
      console.error('Insert failed:', insertError);
      return;
    }


    setFile(null);
    setDocName('');
    setTags('');
    setSelectedClient('');
    setSelectedProject('');
    setUploadModalOpen(false);
    fetchDocuments();
  };

  const handleDownload = async (path) => {
    const { data, error } = await supabase.storage
      .from('client-docs')
      .createSignedUrl(path, 60);
    if (!error) window.open(data.signedUrl, '_blank');
    else console.error('Error creating download link:', error);
  };

  const handleDelete = async (doc) => {
    await supabase.storage.from('client-docs').remove([doc.storage_path]);
    await supabase.from('client_documents').delete().eq('id', doc.id);
    fetchDocuments();
  };

  const handleUpdate = async (docId, updatedFields) => {
    const { error } = await supabase
      .from('client_documents')
      .update(updatedFields)
      .eq('id', docId);

    if (error) {
      console.error('Error updating document:', error);
    } else {
      fetchDocuments();
      setEditingDocId(null);
      setEditFields({ name: '', tags: '', notes: '' });
    }
  };

  const startEditing = (doc) => {
    setEditingDocId(doc.id);
    setEditFields({
      name: doc.name || '',
      tags: (doc.tags || []).join(', '),
      notes: doc.notes || '',
    });
  };

  const cancelEditing = () => {
    setEditingDocId(null);
    setEditFields({ name: '', tags: '', notes: '' });
  };

  // Analytics data
  const totalDocs = docs.length;
  const recentDocs = docs.filter(doc => {
    const uploadDate = new Date(doc.uploaded_at);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return uploadDate >= weekAgo;
  }).length;

  const clientCounts = docs.reduce((acc, doc) => {
    const clientName = doc.clients?.name || 'Unknown';
    acc[clientName] = (acc[clientName] || 0) + 1;
    return acc;
  }, {});

  // Filter documents
  const filteredDocs = docs.filter(doc => {
    const matchesSearch = 
      (doc.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (doc.tags || []).some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesClient = filterClient === 'all' || 
      doc.clients?.name === clients.find(c => c.id === filterClient)?.name;
    
    return matchesSearch && matchesClient;
  });

  if (initialLoading) {
    return (
      <PageLayout title="Client Documents" fullWidth>
        <Box sx={{ maxWidth: '1400px', mx: 'auto', p: 3 }}>
          {/* Analytics Loading */}
          <Box sx={{ mb: 4 }}>
            <LoadingSkeleton height="40px" width="250px" sx={{ mb: 3 }} />
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
              <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
              <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
              <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            </Grid>
          </Box>
          
          {/* Controls Loading */}
          <CardSkeleton />
          
          {/* Documents Loading */}
          <Box sx={{ mt: 4 }}>
            <CardSkeleton height="400px" />
          </Box>
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Client Documents" fullWidth>
      <Box sx={{ 
        maxWidth: '1400px', 
        mx: 'auto', 
        p: 3,
        minHeight: '100vh',
        background: 'linear-gradient(135deg, rgba(0, 168, 132, 0.02) 0%, rgba(248, 250, 252, 1) 100%)'
      }}>
        {/* Hero Header */}
        <Card 
          sx={{ 
            mb: 4,
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(15px)',
            border: '1px solid',
            borderColor: 'success.200',
            borderRadius: 'xl',
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #00A884 0%, #007E33 100%)',
            }
          }}
          className="fade-in"
        >
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                mx: 'auto',
                mb: 2,
                background: 'linear-gradient(135deg, #00A884, #007E33)',
                color: 'white'
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '32px', fontFamily: 'Material Symbols Outlined' }}>
                business_center
              </span>
            </Avatar>
            
            <Typography 
              level="h3" 
              sx={{ 
                mb: 1, 
                background: 'linear-gradient(135deg, #00A884, #007E33)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 700
              }}
            >
              Client Document Library
            </Typography>
            
            <Typography level="body-md" sx={{ color: 'text.secondary', maxWidth: '600px', mx: 'auto' }}>
              Organize and manage client documents by projects with easy search and filtering
            </Typography>
          </Box>
        </Card>

        {/* Analytics Overview */}
        <Card sx={{ mb: 4, borderRadius: 'xl', overflow: 'hidden' }} className="fade-in-up">
          <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'success.100', bgcolor: 'success.50' }}>
            <Typography 
              level="h4" 
              sx={{ 
                color: 'success.700',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                fontWeight: 700
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
                analytics
              </span>
              Document Statistics
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.5 }}>
              Overview of your client document repository
            </Typography>
          </Box>
          
          <Box sx={{ p: 4 }}>
            <Grid container spacing={3}>
              <Grid xs={12} sm={6} md={3}>
                <MetricCard
                  title="Total Documents"
                  value={totalDocs}
                  icon="folder"
                  gradientColors={['#00A884', '#007E33']}
                />
              </Grid>
              <Grid xs={12} sm={6} md={3}>
                <MetricCard
                  title="Recent Uploads"
                  value={recentDocs}
                  icon="upload"
                  gradientColors={['#17a2b8', '#138496']}
                />
              </Grid>
              <Grid xs={12} sm={6} md={3}>
                <MetricCard
                  title="Active Clients"
                  value={Object.keys(clientCounts).length}
                  icon="group"
                  gradientColors={['#6f42c1', '#5a35a3']}
                />
              </Grid>
              <Grid xs={12} sm={6} md={3}>
                <MetricCard
                  title="Projects"
                  value={projects.length}
                  icon="assignment"
                  gradientColors={['#28a745', '#218838']}
                />
              </Grid>
            </Grid>
          </Box>
        </Card>

        {/* Upload & Controls */}
        <Card sx={{ mb: 4, borderRadius: 'xl', overflow: 'hidden' }} className="fade-in-up">
          <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'warning.100', bgcolor: 'warning.50' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography 
                  level="h4" 
                  sx={{ 
                    color: 'warning.700',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    fontWeight: 700
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
                    tune
                  </span>
                  Document Management
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  Upload and organize client documents by project
                </Typography>
              </Box>
              
              <EnhancedButton
                gradient
                onClick={() => setUploadModalOpen(true)}
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    cloud_upload
                  </span>
                }
              >
                Upload Document
              </EnhancedButton>
            </Box>
          </Box>
          
          <Box sx={{ p: 3 }}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid xs={12} sm={4} md={3}>
                <Typography level="body-sm" sx={{ mb: 1, fontWeight: 500 }}>Search Documents</Typography>
                <EnhancedInput
                  placeholder="Search documents, tags..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  startDecorator={
                    <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                      search
                    </span>
                  }
                />
              </Grid>
              
              <Grid xs={12} sm={3} md={2}>
                <FormControl>
                  <FormLabel sx={{ fontWeight: 500 }}>Filter by Client</FormLabel>
                  <Select
                    value={filterClient}
                    onChange={(e, val) => setFilterClient(val)}
                    size="sm"
                    sx={{ '&:focus-within': { borderColor: 'success.500', boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)' }}}
                  >
                    <Option value="all">All Clients</Option>
                    {clients.map(client => (
                      <Option key={client.id} value={client.id}>
                        {client.name}
                      </Option>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            {searchTerm && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'success.50', borderRadius: 'md', border: '1px solid', borderColor: 'success.200' }}>
                <Typography level="body-sm" sx={{ color: 'success.700', fontWeight: 500 }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined', verticalAlign: 'middle', marginRight: '8px' }}>
                    search
                  </span>
                  Found {filteredDocs.length} document{filteredDocs.length !== 1 ? 's' : ''} matching "{searchTerm}"
                </Typography>
              </Box>
            )}
          </Box>
        </Card>

        {/* Documents Table */}
        <Card sx={{ borderRadius: 'xl', overflow: 'hidden' }} className="fade-in-up">
          <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'info.100', bgcolor: 'info.50' }}>
            <Typography 
              level="h4" 
              sx={{ 
                color: 'info.700',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                fontWeight: 700
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
                table_view
              </span>
              Client Documents
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.5 }}>
              Documents organized by client and project
            </Typography>
          </Box>
          
          <Box sx={{ p: 2, background: 'background.surface' }}>
            {filteredDocs.length > 0 ? (
              <JoyTable
                aria-label="client documents"
                hoverRow
                sx={{
                  borderRadius: 8,
                  '& thead th': {
                    fontWeight: 600,
                    backgroundColor: '#e0f2f1',
                    color: '#00695c',
                    textTransform: 'uppercase',
                    fontSize: 12,
                  },
                  '& tbody tr:nth-of-type(odd)': {
                    backgroundColor: '#f9fdfc',
                  },
                  '& tbody tr:nth-of-type(even)': {
                    backgroundColor: '#ffffff',
                  },
                  minWidth: 900,
                }}
              >
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Client</th>
                    <th>Project</th>
                    <th>Tags</th>
                    <th>Uploaded</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(
                    filteredDocs.reduce((acc, doc) => {
                      const client = doc.clients?.name || 'Unknown Client';
                      const project = doc.projects?.name || 'Unassigned Project';
                      if (!acc[client]) acc[client] = {};
                      if (!acc[client][project]) acc[client][project] = [];
                      acc[client][project].push(doc);
                      return acc;
                    }, {})
                  ).map(([clientName, projects]) => (
                    <React.Fragment key={clientName}>
                      <tr>
                        <td colSpan={6} sx={{
                          backgroundColor: '#b2dfdb',
                          color: '#004d40',
                          fontWeight: 'lg',
                          fontSize: 14,
                          py: 1.25,
                          px: 2,
                          borderTop: '2px solid #80cbc4',
                        }}>
                          <Typography level="title-md" sx={{ mt: 1 }}>{clientName}</Typography>
                        </td>
                      </tr>
                      {Object.entries(projects).map(([projectName, groupDocs]) => (
                        <React.Fragment key={projectName}>
                          <tr>
                            <td colSpan={6} sx={{
                              backgroundColor: '#e0f2f1',
                              color: '#00695c',
                              fontWeight: 500,
                              py: 1,
                              px: 2,
                              borderBottom: '1px solid #b2dfdb',
                            }}>
                              <Typography level="body-sm" textColor="text.secondary" sx={{ pl: 2 }}>{projectName}</Typography>
                            </td>
                          </tr>
                          {groupDocs.map((doc) =>
                            editingDocId === doc.id ? (
                              <tr key={doc.id}>
                                <td>
                                  <Input
                                    value={editFields.name}
                                    onChange={(e) =>
                                      setEditFields({ ...editFields, name: e.target.value })
                                    }
                                    size="sm"
                                  />
                                </td>
                                <td>{clientName}</td>
                                <td>{projectName}</td>
                                <td>
                                  <Input
                                    value={editFields.tags}
                                    onChange={(e) =>
                                      setEditFields({ ...editFields, tags: e.target.value })
                                    }
                                    size="sm"
                                    placeholder="Comma-separated"
                                  />
                                </td>
                                <td>{new Date(doc.uploaded_at).toLocaleDateString()}</td>
                                <td>
                                  <Button
                                    size="sm"
                                    onClick={() => {
                                      const updatedTags = editFields.tags
                                        .split(',')
                                        .map((t) => t.trim())
                                        .filter((t) => t);
                                      handleUpdate(doc.id, {
                                        name: editFields.name,
                                        tags: updatedTags,
                                      });
                                    }}
                                  >
                                    Save
                                  </Button>
                                  <Button size="sm" color="neutral" onClick={cancelEditing} sx={{ ml: 1 }}>
                                    Cancel
                                  </Button>
                                </td>
                              </tr>
                            ) : (
                              <tr key={doc.id}>
                                <td><Typography fontWeight={500}>{doc.name}</Typography></td>
                                <td>{clientName}</td>
                                <td>{projectName}</td>
                                <td>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {(doc.tags || []).length > 0
                                      ? doc.tags.map((tag, idx) => (
                                          <Chip size="sm" key={idx} color="success" variant="soft" sx={{ mr: 0.5 }}>
                                            {tag}
                                          </Chip>
                                        ))
                                      : <Typography level="body2" color="neutral" sx={{ opacity: 0.7 }}>—</Typography>}
                                  </Box>
                                </td>
                                <td>{new Date(doc.uploaded_at).toLocaleDateString()}</td>
                                <td>
                                  <IconButton size="sm" variant="plain" onClick={() => handleDownload(doc.storage_path)}>
                                    <DownloadIcon />
                                  </IconButton>
                                  <IconButton size="sm" variant="plain" onClick={() => startEditing(doc)}>
                                    <EditIcon />
                                  </IconButton>
                                  {(userRole === 'admin' || userRole === 'super-admin') && (
                                    <IconButton size="sm" color="danger" variant="plain" onClick={() => handleDelete(doc)}>
                                      <DeleteIcon />
                                    </IconButton>
                                  )}
                                </td>
                              </tr>
                            )
                          )}
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </JoyTable>
            ) : (
              <Card sx={{ p: 6, textAlign: 'center', borderRadius: 'xl' }}>
                <Avatar
                  sx={{
                    bgcolor: 'neutral.100',
                    color: 'neutral.600',
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 3,
                    fontSize: '2.5rem'
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '40px', fontFamily: 'Material Symbols Outlined' }}>
                    {searchTerm ? 'search_off' : 'folder_open'}
                  </span>
                </Avatar>
                <Typography level="h5" sx={{ color: 'neutral.600', mb: 1 }}>
                  {searchTerm ? 'No Documents Found' : 'No Documents Yet'}
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 3 }}>
                  {searchTerm 
                    ? `No documents match your search for "${searchTerm}". Try adjusting your search terms or filters.`
                    : 'Start building your client document library by uploading files organized by client and project.'
                  }
                </Typography>
                {!searchTerm && (
                  <EnhancedButton
                    gradient
                    onClick={() => setUploadModalOpen(true)}
                    startDecorator={
                      <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                        cloud_upload
                      </span>
                    }
                  >
                    Upload First Document
                  </EnhancedButton>
                )}
              </Card>
            )}
          </Box>
        </Card>

        {/* Upload Modal */}
        <Modal open={uploadModalOpen} onClose={() => setUploadModalOpen(false)}>
          <ModalDialog 
            sx={{ 
              maxWidth: '600px',
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
              backdropFilter: 'blur(15px)',
              border: '1px solid',
              borderColor: 'success.200',
              borderRadius: 'xl'
            }}
          >
            <ModalClose />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Avatar
                sx={{
                  bgcolor: 'success.500',
                  color: 'white',
                  width: 48,
                  height: 48
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
                  cloud_upload
                </span>
              </Avatar>
              <Box>
                <Typography level="h4" sx={{ color: 'success.700', fontWeight: 600 }}>
                  Upload Client Document
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  Add a new document to the client library
                </Typography>
              </Box>
            </Box>
            
            <Grid container spacing={2}>
              <Grid xs={12}>
                <EnhancedInput
                  label="Document Name / Description"
                  value={docName}
                  onChange={(e) => setDocName(e.target.value)}
                  placeholder="Brief description of the document..."
                  required
                />
              </Grid>
              
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Select Client *</FormLabel>
                  <Select
                    value={selectedClient}
                    onChange={(e, val) => {
                      setSelectedClient(val);
                      fetchProjects(val);
                      setSelectedProject('');
                    }}
                    sx={{
                      '&:focus-within': {
                        borderColor: 'success.500',
                        boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)',
                      },
                    }}
                  >
                    {clients.map((client) => (
                      <Option key={client.id} value={client.id}>
                        {client.name}
                      </Option>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Select Project (optional)</FormLabel>
                  <Select
                    value={selectedProject}
                    onChange={(e, val) => setSelectedProject(val)}
                    disabled={!selectedClient}
                    sx={{
                      '&:focus-within': {
                        borderColor: 'success.500',
                        boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)',
                      },
                    }}
                  >
                    {projects.map((project) => (
                      <Option key={project.id} value={project.id}>
                        {project.name}
                      </Option>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid xs={12}>
                <FormControl>
                  <FormLabel>Choose File *</FormLabel>
                  <Input
                    type="file"
                    onChange={(e) => setFile(e.target.files[0])}
                    sx={{
                      '&:focus-within': {
                        borderColor: 'success.500',
                        boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)',
                      },
                    }}
                  />
                  {file && (
                    <Typography level="body-xs" sx={{ mt: 1, color: 'success.600' }}>
                      Selected: {file.name}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              
              <Grid xs={12}>
                <EnhancedInput
                  label="Tags"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="tag1, tag2, tag3..."
                  helperText="Separate multiple tags with commas"
                />
              </Grid>
              
              <Grid xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                  <EnhancedButton
                    variant="outlined"
                    onClick={() => setUploadModalOpen(false)}
                  >
                    Cancel
                  </EnhancedButton>
                  <EnhancedButton
                    gradient
                    onClick={handleUpload}
                    loading={loading}
                    disabled={!file || !docName || !selectedClient}
                    startDecorator={
                      <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                        upload
                      </span>
                    }
                  >
                    Upload Document
                  </EnhancedButton>
                </Box>
              </Grid>
            </Grid>
          </ModalDialog>
        </Modal>
      </Box>
    </PageLayout>
  );
};

export default ClientIntDocs;